/**
 * Client side of the two usage routes of the `waadoo-platform` backend plugin
 * (plugins/waadoo-core-backend/src/plugins/waadooPlatform/usageRoutes.ts), which feed the
 * « Ressources » card of the branch page and the CPU and memory columns, and
 * the total line under them, of the environments card
 * (EnvironmentsCard.tsx) on the project page.
 *
 * The types are re-declared rather than imported, for the reason given in
 * `pipelines.ts`: they are the contract of the routes, to be changed on both
 * sides at once — `NamespaceUsageResponse` and `ProjectUsageResponse` here are
 * one half, the interfaces of the same names in `usageRoutes.ts` (and
 * `NamespaceUsage` in `usage.ts`) the other.
 *
 * Who may read them is decided by the backend, per project (the rule of
 * `waadoo-credentials` and `waadoo-mails`); a refusal is a 404, which this client
 * reports as any other failure.
 */

import type { DiscoveryApi, FetchApi } from '@backstage/frontend-plugin-api';
import { usageRatio } from '../home/platform';

/** One resource of a namespace, in millicores or in bytes. */
export interface ResourceFigures {
  /** What the containers use now; null when metrics-server did not say. */
  used: number | null;
  requested: number;
  /** null: some container has no limit, the namespace has no ceiling. */
  limit: number | null;
}

export type VolumeRole = 'attachments' | 'database';

export interface VolumeUsage {
  name: string;
  /** The size ALLOCATED to the volume, not the space used on it. */
  capacityBytes: number | null;
  storageClass: string | null;
  /** Guessed by the backend from the claim's name. */
  role: VolumeRole | null;
}

export interface UsageReading {
  pods: number;
  cpuMillis: ResourceFigures;
  memoryBytes: ResourceFigures;
  volumes: VolumeUsage[];
  /** ISO 8601: when the backend read the namespace (cached up to 30 s). */
  readAt: string;
}

export interface NamespaceUsageResponse {
  namespace: string;
  /** null when the backend could not read the namespace. */
  usage: UsageReading | null;
}

export interface EnvironmentUsage {
  namespace: string;
  branch: string;
  usage: UsageReading | null;
}

export interface ProjectUsageResponse {
  /**
   * By tier — prod, the staging branches, dev, then the ephemeral ones —,
   * each tier by name (the backend's `projectNamespaces`).
   */
  environments: EnvironmentUsage[];
  total: number;
  truncated: boolean;
}

interface RequestOptions {
  fetchApi: FetchApi;
  discoveryApi: DiscoveryApi;
  /** The project of the page — its `waadoo.ovh/project` label. */
  project: string;
}

async function getJson<T>(options: RequestOptions, path: string): Promise<T> {
  // Absolute backend URL: in development the frontend runs on another port.
  const baseUrl = await options.discoveryApi.getBaseUrl('waadoo-platform');
  const response = await options.fetchApi.fetch(
    `${baseUrl}/projects/${encodeURIComponent(options.project)}${path}`,
  );
  if (!response.ok) {
    throw new Error(`Usage API: HTTP ${response.status}`);
  }
  return (await response.json()) as T;
}

/** One environment, by the namespace its ArgoCD Application deploys to. */
export function fetchNamespaceUsage(
  options: RequestOptions & { namespace: string },
): Promise<NamespaceUsageResponse> {
  return getJson(
    options,
    `/namespaces/${encodeURIComponent(options.namespace)}/usage`,
  );
}

/** Every environment of the project, at most 20. */
export function fetchProjectUsage(
  options: RequestOptions,
): Promise<ProjectUsageResponse> {
  return getJson(options, '/usage');
}

/**
 * The share of the limit in use, when there is a bar to draw: both figures
 * known and a limit above zero. Undefined otherwise — the card then states
 * the figures in words, without a bar.
 */
export function usedOfLimit(figures: ResourceFigures): number | undefined {
  const { used, limit } = figures;
  if (used === null || limit === null || limit <= 0) {
    return undefined;
  }
  return usageRatio(used, limit);
}

/** Where the request sits on the bar, as a share of the limit. */
export function requestedOfLimit(figures: ResourceFigures): number | undefined {
  const { limit } = figures;
  if (limit === null || limit <= 0) {
    return undefined;
  }
  return usageRatio(figures.requested, limit);
}

export type MemoryUnit = 'MiB' | 'GiB';

const MiB = 1024 ** 2;
const GiB = 1024 ** 3;

/**
 * The unit a set of byte figures is shown in, all together so that « 512 Mio
 * sur 2 Gio » never happens: GiB from the largest reaching 1 GiB, MiB below.
 */
export function memoryUnit(
  ...bytes: ReadonlyArray<number | null | undefined>
): MemoryUnit {
  const largest = Math.max(
    0,
    ...bytes.filter((value): value is number => typeof value === 'number'),
  );
  return largest >= GiB ? 'GiB' : 'MiB';
}

export function inMemoryUnit(bytes: number, unit: MemoryUnit): number {
  return bytes / (unit === 'GiB' ? GiB : MiB);
}

/** The total line under the environments table. */
export interface UsageTotals {
  cpuMillis: ResourceFigures;
  memoryBytes: ResourceFigures;
  volumesBytes: number;
  /** How many environments were read, and so are in the totals. */
  readable: number;
  /** Some environment could not be read: the totals leave it out. */
  partial: boolean;
}

function sumFigures(figures: ResourceFigures[]): ResourceFigures {
  let used: number | null = 0;
  let limit: number | null = 0;
  let requested = 0;
  for (const item of figures) {
    used = used === null || item.used === null ? null : used + item.used;
    limit = limit === null || item.limit === null ? null : limit + item.limit;
    requested += item.requested;
  }
  return { used, requested, limit };
}

/**
 * The environments added up. A consumption unknown for one makes the total
 * unknown, and a limit unbounded for one makes it unbounded: a sum of the
 * others would be presented as the whole. An environment that could not be
 * read at all is left out, and `partial` says so.
 */
export function totalUsage(environments: EnvironmentUsage[]): UsageTotals {
  const readings = environments
    .map(environment => environment.usage)
    .filter((usage): usage is UsageReading => usage !== null);
  return {
    cpuMillis: sumFigures(readings.map(usage => usage.cpuMillis)),
    memoryBytes: sumFigures(readings.map(usage => usage.memoryBytes)),
    volumesBytes: readings.reduce(
      (total, usage) =>
        total +
        usage.volumes.reduce(
          (sum, volume) => sum + (volume.capacityBytes ?? 0),
          0,
        ),
      0,
    ),
    readable: readings.length,
    partial: readings.length < environments.length,
  };
}
